import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, Lock, Sparkles } from 'lucide-react';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authType, setAuthType] = useState('magic_link');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password, authType);
      
      if (result.magic_link) {
        toast.success(result.message);
        toast.info(`Magic link: ${result.magic_link}`, { duration: 10000 });
      } else if (result.success) {
        toast.success('Login successful!');
        navigate('/client/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="text-3xl font-bold font-['Syne'] text-white mb-2" data-testid="login-logo">
              Agency<span className="text-indigo-500">Pro</span>
            </h1>
          </Link>
          <p className="text-slate-400">Welcome back to your client portal</p>
        </div>

        <div className="glass rounded-2xl p-8 glow">
          <div className="flex gap-2 mb-6" data-testid="auth-type-toggle">
            <button
              onClick={() => setAuthType('magic_link')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                authType === 'magic_link'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
              data-testid="magic-link-tab"
            >
              <Sparkles className="inline mr-2" size={16} />
              Magic Link
            </button>
            <button
              onClick={() => setAuthType('password')}
              className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                authType === 'password'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
              data-testid="password-tab"
            >
              <Lock className="inline mr-2" size={16} />
              Password
            </button>
          </div>

          <form onSubmit={handleSubmit} data-testid="login-form">
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    data-testid="login-email-input"
                    className="pl-10 bg-slate-950 border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 text-white placeholder:text-slate-600"
                  />
                </div>
              </div>

              {authType === 'password' && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required={authType === 'password'}
                      data-testid="login-password-input"
                      className="pl-10 bg-slate-950 border-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 text-white placeholder:text-slate-600"
                    />
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full bg-white text-black hover:bg-slate-200 font-semibold py-3 rounded-full transition-all duration-300"
              >
                {loading ? 'Processing...' : authType === 'magic_link' ? 'Send Magic Link' : 'Sign In'}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-400">
              Admin? <Link to="/admin/login" className="text-indigo-400 hover:text-indigo-300" data-testid="admin-login-link">Login here</Link>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-slate-400 hover:text-white transition-colors text-sm" data-testid="back-home-link">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
